"use client";
import Image from "next/image";
import SmoothImage from "@/components/SmoothImage";
import { renderTextWithBreaks } from "@/utils/stripHTML";

export interface OverviewStat {
  value: string;
  label: string;
}

export interface OverviewSpec {
  label: string;
  value: string;
  subtext?: string;
}

export interface ProjectOverviewData {
  left: {
    image: string;
    tag: string;
    title: string;
    stats: OverviewStat[];
  };
  right: {
    sectionIndex?: string;
    totalSections?: string;
    tag: string;
    title: string;
    description: string;
    specs: OverviewSpec[];
    ctaText: string;
    ctaHref?: string;
    disclaimer?: string;
  };
}

interface ProjectOverviewSectionProps {
  data: ProjectOverviewData;
  onCtaClick?: () => void;
}

export default function ProjectOverviewSection({
  data,
  onCtaClick,
}: ProjectOverviewSectionProps) {
  if (!data) return null;

  const { left, right } = data;

  return (
    <section
      id="tong-quan-du-an"
      className="relative w-full min-h-screen lg:h-screen flex flex-col lg:flex-row overflow-hidden bg-primaryDark select-none"
    >
      {/* ================= LEFT COLUMN: IMAGE & OVERLAY STATS (55% WIDTH) ================= */}
      <div className="w-full lg:w-[65%] min-h-[360px] sm:min-h-[440px] md:min-h-[480px] lg:h-full relative overflow-hidden bg-black/40 flex-shrink-0">
        <SmoothImage
          src={left.image}
          alt={left.title || "Dragon Home Architecture"}
          fill
          sizes="(max-width: 1024px) 100vw, 65vw"
          priority
          className="object-cover object-center"
        />

        {/* GRADIENT OVERLAY FOR TEXT CONTRAST */}
        <div className="hidden lg:block absolute bottom-0 left-0 right-0 h-1/3 bg-gradient-to-t from-black/85 via-black/35 to-transparent pointer-events-none z-10" />

        {/* BOTTOM CONTENT OVERLAY */}
        <div className="absolute bottom-0 inset-x-0 px-6 sm:px-10 lg:px-14 xl:px-16 pt-6 pb-4 sm:pb-6 lg:pb-8 xl:pb-8 z-20 flex flex-col justify-end text-white">
          <p className="text-[10px] sm:text-xs font-semibold tracking-[0.2em] uppercase text-[#EBDDBF] font-sans mb-1.5">
            {left.tag}
          </p>

          <h3 className="text-xl sm:text-2xl md:text-3xl font-lora font-normal text-white leading-tight max-w-lg mb-4 sm:mb-6">
            {renderTextWithBreaks(left.title)}
          </h3>

          {/* STATS COUNTER GRID AT BOTTOM */}
          {left.stats && left.stats.length > 0 && (
            <div className="pt-3 border-t border-white/20 grid grid-cols-3 gap-3 sm:gap-6">
              {left.stats.map((stat, idx) => (
                <div key={idx} className="flex flex-col">
                  <span className="text-xl sm:text-2xl md:text-3xl font-lora font-normal text-white">
                    {stat.value}
                  </span>
                  <span className="text-[8px] sm:text-[9px] font-sans tracking-widest text-white/70 uppercase mt-0.5">
                    {stat.label}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* ================= RIGHT COLUMN: PAPER TEXTURE DATA TABLE (45% WIDTH, VERTICALLY CENTERED WITH TOP HEADER CLEARANCE) ================= */}
      <div className="w-full lg:w-[35%] min-h-[auto] lg:h-full bg-secondary-light text-primaryDark flex flex-col justify-start lg:justify-center px-4 sm:px-6 lg:px-8 xl:px-10 pt-16 sm:pt-20 lg:pt-24 pb-8 sm:pb-10 relative overflow-y-auto lg:overflow-hidden">
        {/* PAPER TEXTURE BACKGROUND IMAGE */}
        <div className="absolute inset-0 z-0 pointer-events-none">
          <Image
            src="/images/background_2.png"
            alt="Paper Texture Background"
            fill
            sizes="(max-width: 1024px) 100vw, 35vw"
            priority
            className="object-cover object-center"
          />
        </div>

        {/* MAIN CONTAINER ANCHORED FROM BOTTOM WITH EXPLICIT SPACING */}
        <div className="relative z-10 flex flex-col space-y-2 sm:space-y-3 mt-4 lg:mt-6">
          {/* HEADER ROW: TAG & COUNTER & TITLE & DESC */}
          <div>
            <div className="flex items-center justify-between mb-1">
              <div className="flex items-center gap-3">
                <span className="w-6 h-[1px] bg-primaryDark/40" />
                <span className="text-[11px] sm:text-xs font-semibold uppercase tracking-[0.2em] text-primaryDark/70 font-sans">
                  {right.tag}
                </span>
              </div>
            </div>

            {/* MAIN HEADING */}
            <h2
              className="text-3xl sm:text-4xl md:text-5xl lg:text-5xl font-lora font-normal text-primaryDark tracking-tight mb-1.5 title-leading"
              style={{ lineHeight: 1.15 }}
            >
              {renderTextWithBreaks(right.title)}
            </h2>

            {/* DESCRIPTION */}
            <p className="text-xs sm:text-sm font-light text-primaryDark/80 font-sans leading-relaxed max-w-lg">
              {renderTextWithBreaks(right.description)}
            </p>
          </div>

          {/* PROJECT OVERVIEW SPECS TABLE */}
          {right.specs && right.specs.length > 0 && (
            <div className="space-y-0 border-t border-primaryDark/15 pt-1 mt-1">
              {right.specs.map((spec, idx) => (
                <div
                  key={idx}
                  className="flex items-baseline justify-between py-1 sm:py-1.5 border-b border-primaryDark/15"
                >
                  <span className="text-[9.5px] sm:text-[10.5px] md:text-xs font-semibold uppercase tracking-wider text-primaryDark/60 font-sans w-5/12 pr-2">
                    {spec.label}
                  </span>
                  <div className="w-7/12 text-right">
                    <span className="font-lora text-[11px] sm:text-xs md:text-[13px] font-normal text-primaryDark leading-tight block">
                      {spec.value}
                    </span>
                    {spec.subtext && (
                      <span className="text-[8.5px] sm:text-[9.5px] font-sans text-primaryDark/60 font-normal leading-tight block mt-0.5">
                        {spec.subtext}
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* BOTTOM CTA LINK & DISCLAIMER */}
          <div className="pt-1">
            <a
              href={right.ctaHref || "#mat-bang"}
              onClick={(e) => {
                if (onCtaClick) {
                  e.preventDefault();
                  onCtaClick();
                }
              }}
              className="inline-flex items-center gap-2 group cursor-pointer border-b border-primaryDark/30 hover:border-primaryDark pb-0.5 transition-colors mb-1.5"
            >
              <span className="text-xs font-semibold uppercase tracking-[0.18em] text-primaryDark group-hover:text-primary transition-colors font-sans">
                {right.ctaText}
              </span>
              <Image
                src="/icons/arrow_outward.svg"
                alt="arrow"
                width={14}
                height={14}
                className="w-3.5 h-3.5 text-primaryDark group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform object-contain"
              />
            </a>

            {right.disclaimer && (
              <p className="text-[9px] sm:text-[10px] font-sans text-primaryDark/50 italic leading-tight">
                {right.disclaimer}
              </p>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
